import { useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { useHistory, Redirect } from 'react-router-dom';
import { ZodError } from 'zod';
import { useAuth } from '@/lib/auth/useAuth';
import {
  createPublication,
  publicationKinds,
  publicationSchema,
  titleToSlug,
} from '@/lib/api/publications';
import { createDocument } from '@/lib/api/documents';
import { Logo } from '@/components/ui/Logo';

const KIND_LABEL: Record<string, string> = {
  article: 'Article', essay: 'Essay', book: 'Book', series: 'Series',
};

export default function NewPublicationPage() {
  const { status } = useAuth();
  const history = useHistory();

  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [kind, setKind] = useState<'article' | 'essay' | 'book' | 'series'>('article');
  const [visibility, setVisibility] = useState<'public' | 'unlisted' | 'private'>('public');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  if (status === 'unauthenticated') return <Redirect to="/auth" />;
  if (status === 'needs-handle') return <Redirect to="/auth/claim-handle" />;

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugEdited) {
      setSlug(titleToSlug(value));
    }
  }

  function handleSlugChange(value: string) {
    setSlugEdited(true);
    setSlug(value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  }

  function validate() {
    try {
      publicationSchema.parse({ title, slug, kind, visibility });
      setFieldErrors({});
      return true;
    } catch (e) {
      if (e instanceof ZodError) {
        const errs: Record<string, string> = {};
        for (const issue of e.errors) {
          if (issue.path[0]) errs[String(issue.path[0])] = issue.message;
        }
        setFieldErrors(errs);
      }
      return false;
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const pub = await createPublication({ title, slug, kind, visibility });
      // Auto-create first document so the user lands directly in the editor.
      const { documentId } = await createDocument(pub.id, title);
      history.replace(`/edit/${documentId}`);
    } catch (err: unknown) {
      const pgErr = err as { message?: string; code?: string };
      const msg = pgErr?.message ?? (err instanceof Error ? err.message : 'Something went wrong.');
      if (pgErr?.code === '23505' || msg.includes('unique') || msg.includes('already')) {
        setFieldErrors(prev => ({ ...prev, slug: 'You already have a publication with that slug.' }));
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-2xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.goBack()}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
            <span className="text-sm font-semibold text-gray-700">New publication</span>
          </div>
        </header>

        <main className="mx-auto max-w-2xl px-5 py-10">
          <form onSubmit={submit} className="flex flex-col gap-6">
            {/* Title */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="title">
                Title
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={e => handleTitleChange(e.target.value)}
                maxLength={200}
                placeholder="My publication"
                autoFocus
                className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
              />
              {fieldErrors.title && (
                <p className="text-xs text-red-500">{fieldErrors.title}</p>
              )}
            </div>

            {/* Slug */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="slug">
                Slug
              </label>
              <div className="flex items-center rounded-lg border border-gray-200 focus-within:border-[#1a56a0] focus-within:ring-1 focus-within:ring-[#1a56a0]">
                <span className="pl-3.5 text-sm text-gray-400">/@you/</span>
                <input
                  id="slug"
                  type="text"
                  value={slug}
                  onChange={e => handleSlugChange(e.target.value)}
                  maxLength={100}
                  placeholder="my-publication"
                  className="min-w-0 flex-1 border-none bg-transparent py-2.5 pr-3.5 text-sm outline-none"
                />
              </div>
              {fieldErrors.slug && (
                <p className="text-xs text-red-500">{fieldErrors.slug}</p>
              )}
              {!fieldErrors.slug && slug.length > 0 && (
                <p className="text-xs text-gray-400">Slug cannot be changed after creation.</p>
              )}
            </div>

            {/* Kind */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">Kind</label>
              <div className="flex flex-wrap gap-2">
                {publicationKinds.map(k => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition-colors ${
                      kind === k
                        ? 'border-[#2f3e46] bg-[#2f3e46] text-white'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {KIND_LABEL[k]}
                  </button>
                ))}
              </div>
              {fieldErrors.kind && (
                <p className="text-xs text-red-500">{fieldErrors.kind}</p>
              )}
            </div>

            {/* Visibility */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="visibility">
                Visibility
              </label>
              <select
                id="visibility"
                value={visibility}
                onChange={e => setVisibility(e.target.value as typeof visibility)}
                className="rounded-lg border border-gray-200 px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#1a56a0] focus:ring-1 focus:ring-[#1a56a0]"
              >
                <option value="public">Public — visible to everyone</option>
                <option value="unlisted">Unlisted — accessible by link only</option>
                <option value="private">Private — only you can see it</option>
              </select>
            </div>

            {error && (
              <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
            )}

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => history.goBack()}
                className="rounded-lg border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-lg bg-[#2f3e46] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33] disabled:opacity-50"
              >
                {saving ? 'Creating…' : 'Create & start writing'}
              </button>
            </div>
          </form>
        </main>
      </IonContent>
    </IonPage>
  );
}
